import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, buttonClasses } from '@nexa/ui';
import { useCopy } from '@/core/i18n';
import { ContactDialogContext } from './contactDialog.ts';

/**
 * Dialog một dòng cho sáu nút CTA. Trang chưa có luồng đăng ký hay đặt lịch thật, nên nút phải
 * nói ra điều đó thay vì cuộn xuống `#cta` rồi để người dùng tự đoán (hành vi trước đây).
 *
 * ── Vì sao ở `core/` chứ không ở `features/` hay `packages/ui` ────────────────────────────────
 * Hộ dùng nằm ở CẢ HAI tầng: `core/layout` (Header, MobileDrawer) và `features/*` (hero, cta).
 * `contract.md` §1.4 cấm `core/` phụ thuộc `features/`, nên nó không thể ở `features/`. Và nó
 * không phải TỪ VỰNG dùng chung — nó là một dialog cụ thể với một câu cụ thể, tức nội dung ứng
 * dụng, nên `packages/ui` cũng sai chỗ.
 *
 * ── Vì sao KHÔNG dùng <dialog> của trình duyệt ───────────────────────────────────────────────
 * `showModal()` cần một hiệu ứng phụ mệnh lệnh sau khi node đã vào DOM, và `::backdrop` không
 * nhận biến CSS của `:root` trong mọi trình duyệt hiện hành. Khuôn portal dưới đây đã chạy sẵn ở
 * `MobileDrawer`; dùng lại nó rẻ hơn và có ít cách hỏng hơn.
 */
function Dialog({ onClose }: { readonly onClose: () => void }) {
  const copy = useCopy();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // Khoá cuộn nền. Lưu giá trị cũ thay vì đặt cứng '' — nếu có thứ khác cũng đang khoá cuộn
    // thì trả về '' là âm thầm mở khoá của người ta. Cùng lý lẽ với `MobileDrawer`.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[60]">
      {/*
        Lớp phủ là phần tử RIÊNG, không phải nền của hộp căn giữa: bấm ra ngoài để đóng phải
        nhắm đúng lớp phủ, còn hộp căn giữa thì trải rộng cả màn và bấm vào khoảng trống cạnh
        thẻ cũng sẽ trúng nó.

        `--scrim` gốc `surface-black` chứ không phải `surface-navy` — bốn trong sáu nút mở dialog
        nằm trên nền navy, nên scrim gốc navy phủ lên nền navy là gần như vô hình.
      */}
      <button
        type="button"
        aria-label={copy.contact.close}
        onClick={onClose}
        className="absolute inset-0 w-full cursor-default bg-[var(--scrim)] backdrop-blur-[14px]"
      />

      <div className="pointer-events-none absolute inset-0 grid place-items-center px-gutter">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-dialog-title"
          aria-describedby="contact-dialog-body"
          className="pointer-events-auto relative w-full max-w-[24rem] overflow-hidden rounded-card-lg bg-surface shadow-chat-panel"
        >
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={copy.contact.close}
            className="absolute top-2.5 right-2.5 grid size-11 place-items-center rounded-pill text-ink-500 transition [transition-duration:var(--duration-hover)] hover:bg-surface-muted hover:text-ink-900"
          >
            <Icon name="x" size={18} />
          </button>

          <div className="px-8 pt-9 pb-7 text-center">
            {/*
              Icon là TRANG TRÍ: câu ngay dưới đã nói đủ, nên `Icon` tự khai `aria-hidden` khi
              không có `label` và ta KHÔNG đặt thuộc tính gạch ngang lên component — cổng
              `dashAttrOnComponent` bắt đúng chuyện đó, và prop kiểu ấy rơi im lặng lúc render.

              Vòng nền `surface-muted` + icon `brand-600`, bo `radius-card` — ba token đã có,
              không thêm giá trị nào. Icon lấy từ registry ĐANG CÓ: `Icon.test.tsx` canh registry
              khớp KHÍT tập icon của thiết kế, nên thêm một icon mới là làm đỏ cổng đó.
            */}
            <span className="mx-auto mb-5 grid size-12 place-items-center rounded-card bg-surface-muted text-brand-600">
              <Icon name="messages-square" size={22} />
            </span>

            <h2 id="contact-dialog-title" className="text-title-lg font-bold text-ink-900">
              {copy.contact.title}
            </h2>

            <p id="contact-dialog-body" className="mt-2 text-body text-ink-600">
              {copy.contact.before}
              <a
                href={`mailto:${copy.contact.email}`}
                className="font-semibold text-brand-600 underline underline-offset-2 transition [transition-duration:var(--duration-hover)] hover:text-brand-900"
              >
                {copy.contact.email}
              </a>
              {copy.contact.after}
            </p>
          </div>

          {/*
            Chân dialog tách bằng `border-ink-100` — cùng nét 1px mà `Header` và `Card` dùng.
            ĐÚNG MỘT nút: dialog này không hỏi người dùng chọn gì, nó chỉ đưa một đường đi tiếp.
            Đường thoát vẫn có ba lối: nút ×, phím Escape, và bấm ra lớp phủ.
          */}
          <div className="border-t border-ink-100 px-6 py-5">
            <a
              href={`mailto:${copy.contact.email}`}
              className={`${buttonClasses('primary', 'md')} w-full gap-2`}
            >
              <Icon name="send-horizontal" size={17} />
              {copy.contact.footerCta}
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Dựng ở `RootLayout` — mọi nút CTA của trang lấy hàm mở qua `useContactDialog()`. */
export function ContactDialogProvider({ children }: { readonly children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);

  const handleOpen = useCallback(() => {
    // Nhớ phần tử đang focus để TRẢ VỀ khi đóng. Không trả thì sau khi đóng, focus rơi về
    // `<body>` và lượt Tab kế tiếp bắt đầu lại từ đầu trang — người dùng bàn phím mất chỗ.
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    opener.current?.focus();
    opener.current = null;
  }, []);

  // `useMemo` vì context value là hàm: một hàm mới mỗi lượt render sẽ render lại MỌI hộ tiêu thụ,
  // và hộ tiêu thụ ở đây gồm cả header lẫn hai section lớn.
  const value = useMemo(() => handleOpen, [handleOpen]);

  return (
    <ContactDialogContext value={value}>
      {children}
      {open ? <Dialog onClose={handleClose} /> : null}
    </ContactDialogContext>
  );
}
